import { Chip, Typography } from '@mui/material';

// Components
import { IContributeCompany } from 'src/utils/typings/response/contribute';
import Table from 'src/components/Table';

// Utilities
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { convertSnakeCaseToUpperFirst, convertToStartCase, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { formatUTCTime } from 'src/utils/helpers/dayjs';
import {
  ContributeTypes,
  getColorContributionStatus,
  getContributionTypeLabel,
  StatusContribution,
} from 'src/features/Contributions/config';
import { useNavigate } from 'react-router-dom';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { useQueryParams } from 'src/hooks/useQueryParam';

interface IListCompanyContributionProps {
  listContribution: IContributeCompany[];
  isFetching: boolean;
}

const ContributeCompanyList = ({ listContribution, isFetching }: IListCompanyContributionProps) => {
  const navigate = useNavigate();
  const { tab = ContributeTypes.Company } = useQueryParams();

  const configColumn: ConfigCell<IContributeCompany>[] = [
    {
      id: 'created_at',
      disablePadding: true,
      label: 'Created At',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {formatUTCTime(FORMAT_DATE_TIME.FULL_ONLY_DATE, row.created_at)}
          </Typography>
        );
      },
    },
    {
      id: 'type_of_contribution',
      disablePadding: true,
      label: 'Types of Contribution',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {getContributionTypeLabel(row.type_of_contribution)}
          </Typography>
        );
      },
    },
    {
      id: 'name',
      disablePadding: true,
      label: 'Company Name',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row.name)}
          </Typography>
        );
      },
    },
    {
      id: 'status',
      disablePadding: true,
      label: 'Status',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Chip
            label={convertToStartCase(row.status)}
            size="small"
            color={getColorContributionStatus(row?.status ?? StatusContribution.Trash)}
          />
        );
      },
    },
    {
      id: 'relationship_to_data',
      disablePadding: true,
      label: 'Relationship to Data',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {convertSnakeCaseToUpperFirst(row.relationship_to_data)}
          </Typography>
        );
      },
    },
    {
      id: 'note',
      disablePadding: true,
      label: 'Note',
      align: 'left',
      cell: (row: IContributeCompany) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row.note)}
          </Typography>
        );
      },
    },
  ];

  const navigateToDetail = (data: IContributeCompany) => {
    if (data?.id) {
      navigate(AuthRouteKeys.ContributionDetail.replace(':id?', data.id.toString()).replace(':contributionType', tab));
    }
  };

  return (
    <Table
      maxRow={10}
      isPagination={false}
      loading={isFetching}
      data={getArrayDefault(listContribution)}
      configColumn={configColumn}
      onRowClick={(data: IContributeCompany) => navigateToDetail(data)}
      dataKey="id"
    />
  );
};

export default ContributeCompanyList;
